import { getSideString } from '@/core/utils';

const REBATE_PERCENT = 0.001;

// Rebates are in closed testing — only these usernames see any rebate UI.
// Keep in sync with rebateUserRates in remote config, which decides who is
// actually PAID at grading; this list only decides who SEES the UI.
const REBATE_TEST_USERNAMES = [
  'poly2',
  'senorsmoke',
  '6900',
  '6901',
  'mongoose',
];

export function canSeeRebates(username) {
  return REBATE_TEST_USERNAMES.includes(username);
}

export function getRebatePercent() {
  return REBATE_PERCENT;
}

export function calculateRebateAmount(risk, win, rate = REBATE_PERCENT) {
  const nRisk = parseFloat(risk) || 0;
  const nWin = parseFloat(win) || 0;
  if (nRisk <= 0 || nWin <= 0) return 0;
  return Math.min(nRisk, nWin) * rate;
}

export function formatRebate(amount) {
  return `+$${parseFloat(amount).toFixed(2)}`;
}

// Maker-perspective side label for a pending (matched-offer) fill.
// showodds=true is deliberate: for spread/total that flag gates the LINE
// number (e.g. "Fever +3.5", "Over 210.5"), not American odds — so it keeps
// the label as complete as the server-computed credited label. The fill's
// American odds are shown separately. Guarded so a malformed row can't blank
// the panel.
//
// Props note: elsewhere the app uses getSideStringProps ("<eventName> O25.5")
// for PROPS leagues, but that is deliberately NOT used here. This table shows
// the eventName in its own Game column, so getSideStringProps would duplicate
// it; and the server's credited side (makerSideLabel) returns the plain
// "Over/Under {total}" for props too. Routing props through getSideString's
// total branch keeps pending rows byte-identical to credited rows and avoids
// the duplication. (No props rebates exist yet — this is the intended shape
// for when they do.)
function safeSideString(game, row) {
  if (!game) return null;
  // Moneyline/spread need participants; totals don't. Bail only when a
  // participant-dependent type is missing them.
  const needsParticipants = row.type === 'moneyline' || row.type === 'spread';
  if (needsParticipants && !Array.isArray(game.participants)) return null;
  try {
    return getSideString(game, row, true) || null;
  } catch {
    return null;
  }
}

// A graded position's net fees: commission (negative) offset by the maker
// rebate credit (positive). This is what the Fees column shows, so the Fees
// sort must key on the same value. Rows without a rebate are unchanged.
export function netFees(position) {
  return (position.netCommission || 0) + (position.rebate || 0);
}

// Fees-column display: negatives already read as charges (e.g. "-0.50"), but
// when a rebate offsets commission the net can be positive — show an explicit
// "+" so a credit isn't mistaken for a charge. Zero stays unsigned.
export function formatFees(value) {
  const n = Number.parseFloat(value) || 0;
  const rounded = Number(n.toFixed(2));
  // Number() collapses -0.00 → 0, so a value that rounds to zero prints "0.00"
  // rather than "-0.00", and only genuine positives get the "+".
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded.toFixed(2)}`;
}

// Rebates are paid per fill by the backgroundjobs grader at grading time
// (DisplayTransaction type 'rebate', min(risk, win) × the user's configured
// rate). The history therefore has two kinds of rows:
//   credited — real ledger entries from user/getRebates
//   pending  — matched-but-ungraded maker fills (origin 'offer' rows from
//              user/getMatchedBets) that will pay out when the game grades
// Volume is the rebate-eligible volume, min(risk, win) — for credited rows
// it's derived back from amount / rate.
export function buildRebateHistory({ rebates, matchedBets, rebateRate }) {
  const credited = (rebates || []).map((r) => {
    const amount = parseFloat(r.amount) || 0;
    return {
      id: String(r.id),
      gameId: r.gameID ? String(r.gameID) : null,
      date: r.createdAt,
      game: r.game?.eventName || '',
      league: r.game?.league || '',
      type: r.type,
      odds: r.odds,
      // Maker-perspective side label computed server-side (the ledger row
      // doesn't carry the game's participants for a client-side derivation).
      side: r.side || null,
      offerVolume: rebateRate > 0 ? amount / rebateRate : null,
      rebateAmount: amount,
      status: 'credited',
    };
  });

  // Without a configured rate nothing will be paid at grading, so there are
  // no pending rebates to show.
  const pending = !(rebateRate > 0)
    ? []
    : (matchedBets || [])
        .filter((row) => row.origin === 'offer')
        .filter((row) => !row.adminRefund && row.outcome !== 'refunded')
        .map((row) => {
          const risk = parseFloat(row.risk) || 0;
          const win = parseFloat(row.win) || 0;
          return {
            id: String(row.txID || row.ticketNumber),
            gameId: row.game?.id ? String(row.game.id) : null,
            date: row.matchedTime,
            game: row.game?.eventName || '',
            league: row.game?.league || '',
            type: row.type,
            odds: row.odds,
            // Matched-offer rows already carry the maker-perspective side
            // fields + participants, so safeSideString resolves the full
            // label (team + spread/total line) to match the credited rows.
            side: safeSideString(row.game, row),
            offerVolume: Math.min(risk, win),
            rebateAmount: calculateRebateAmount(risk, win, rebateRate),
            status: 'pending',
          };
        })
        .filter((row) => row.rebateAmount > 0);

  return [...credited, ...pending].sort(
    (a, b) => new Date(b.date) - new Date(a.date),
  );
}

// One row per game: fills collapse into a combined volume and rebate total,
// with the individual fills kept on `fills` so the row can expand to show the
// per-fill breakdown (each carries its own type/odds/volume/rebate). The
// game-level rollup intentionally drops type/odds — they vary per fill. A game
// grades atomically, so rows are normally all-credited or all-pending; if both
// appear, the group stays 'pending' until everything has paid.
export function groupRebateHistoryByGame(history) {
  const groups = new Map();
  history.forEach((row) => {
    const key = row.gameId || `${row.game}|${row.league}`;
    const group = groups.get(key);
    if (!group) {
      groups.set(key, {
        id: String(key),
        date: row.date,
        game: row.game,
        league: row.league,
        offerVolume: row.offerVolume,
        hasVolume: row.offerVolume != null,
        rebateAmount: row.rebateAmount,
        fillCount: 1,
        status: row.status,
        fills: [row],
      });
      return;
    }
    group.offerVolume = (group.offerVolume || 0) + (row.offerVolume || 0);
    group.hasVolume = group.hasVolume || row.offerVolume != null;
    group.rebateAmount += row.rebateAmount;
    group.fillCount += 1;
    group.fills.push(row);
    if (new Date(row.date) > new Date(group.date)) group.date = row.date;
    if (row.status === 'pending') group.status = 'pending';
  });
  return [...groups.values()]
    .map(({ hasVolume, ...group }) => ({
      ...group,
      offerVolume: hasVolume ? group.offerVolume : null,
      fills: group.fills
        .slice()
        .sort((a, b) => new Date(b.date) - new Date(a.date)),
    }))
    .sort((a, b) => new Date(b.date) - new Date(a.date));
}

// Earned/volume totals count only credited rows (real ledger money);
// pendingTotal surfaces what's still waiting on grading. The windows are
// rolling (now − 7/30 days), not calendar week/month — labels must match.
//
// When the endpoint provides all-time `serverTotals` ({ totalEarned,
// last7Days, last30Days }), those win: the history rows are windowed
// (?sinceDays) and can't reproduce all-time sums. Volume is derived back
// from earnings at the configured rate, same approximation as per-row
// offerVolume. Without serverTotals (older API), everything is computed
// from the rows, which are all-time in that case.
export function buildRebateSummary(history, serverTotals = null, rate = null) {
  const pendingTotal = history
    .filter((r) => r.status === 'pending')
    .reduce((sum, r) => sum + r.rebateAmount, 0);

  if (serverTotals) {
    const totalEarned = parseFloat(serverTotals.totalEarned) || 0;
    return {
      totalEarned,
      last7Days: parseFloat(serverTotals.last7Days) || 0,
      last30Days: parseFloat(serverTotals.last30Days) || 0,
      totalVolume: rate > 0 ? totalEarned / rate : 0,
      pendingTotal,
    };
  }

  const now = new Date();
  const sevenDaysAgo = new Date(now - 1000 * 60 * 60 * 24 * 7);
  const thirtyDaysAgo = new Date(now - 1000 * 60 * 60 * 24 * 30);

  const credited = history.filter((r) => r.status === 'credited');
  const totalEarned = credited.reduce((sum, r) => sum + r.rebateAmount, 0);
  const last7Days = credited
    .filter((r) => new Date(r.date) >= sevenDaysAgo)
    .reduce((sum, r) => sum + r.rebateAmount, 0);
  const last30Days = credited
    .filter((r) => new Date(r.date) >= thirtyDaysAgo)
    .reduce((sum, r) => sum + r.rebateAmount, 0);
  const totalVolume = credited.reduce(
    (sum, r) => sum + (r.offerVolume || 0),
    0,
  );

  return { totalEarned, last7Days, last30Days, totalVolume, pendingTotal };
}
